import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ThreadViewWrapper from 'conversations-thread-view/thread-view/components/ThreadViewWrapper';
// bend-lint-disable-next-line strict-exports
import { useComposerTrackingContext } from 'composer-ui/usage-tracking/hooks/useComposerTrackingContext';
import { useConversationIdByThreadId } from '../../crm-integration/hooks/useConversationIdByThreadId';
import { getCurrentViewId } from '../../thread-list/selectors/getCurrentViewId';
import { getUsageTracker, getBeaconUsageTracker } from '../../usage-tracking/clients/usageTracker';
import { AsyncUIAvatar } from 'svh-workspace-ui-lib/code-splitting/public';
import { getThreadListMemberByThreadId } from '../../thread-list-member/selectors/getThreadListMemberByThreadId';
import { useThreadViewRefSyncing } from '../hooks/useThreadViewRefSyncing';
import { getAuth } from '../../auth/selectors/getAuth';
import { getInitialThreadViewState } from 'conversations-inbox-lib/initialThreadView/public/selectors';
import { useViewCanSeeDeletedMessages } from '../../thread-list/hooks/useViewCanSeeDeletedMessages';
import { getCurrentInboxId } from 'conversations-inbox-lib/unified-inbox/public/selectors';
import { useTicketWatcher } from '../../tickets/hooks/useTicketWatcher';
import { useMemberViewMembershipListener } from '../../custom-views/hooks/useMemberViewMembershipListener';
import { arePropertiesSupportedThreadProps, arePropertiesSupportedTicketProps } from 'find-and-filter-data/view-member-optimistic-updates/public';
import { useAssociatedTickets } from 'svh-tickets-lib/tickets/public/hooks';
import { getChannels, getVid } from 'find-and-filter-data/view-members-schema/protected';
import { useVisitor } from 'find-and-filter-data/visitors/public';
import { jsx as _jsx } from "react/jsx-runtime";
const ThreadViewProvider = ({
  children,
  threadId
}) => {
  const threadViewRef = useThreadViewRefSyncing({
    threadId
  });
  const {
    conversationId
  } = useConversationIdByThreadId({
    threadId
  });
  const {
    associatedTicket
  } = useAssociatedTickets({
    conversationId
  });
  const threadListMember = useSelector(state => getThreadListMemberByThreadId(state, {
    threadId
  }));
  const viewId = useSelector(getCurrentViewId);
  const omnibusData = useSelector(getInitialThreadViewState);
  const auth = useSelector(getAuth);
  const showDeletedMessages = useViewCanSeeDeletedMessages();
  const ticketCount = associatedTicket ? 1 : 0;
  const inboxId = useSelector(getCurrentInboxId);
  const {
    evaluateListMembership
  } = useMemberViewMembershipListener({
    threadId,
    viewId
  });
  const handleTicketChange = useCallback((differences, ticket) => {
    if (arePropertiesSupportedTicketProps(differences)) {
      evaluateListMembership({
        ticket
      });
    }
  }, [evaluateListMembership]);
  useTicketWatcher({
    ticket: associatedTicket,
    onChange: handleTicketChange
  });
  const usageTracker = getUsageTracker();
  const lifecycle = useMemo(() => ({
    onThreadTrackingContextChange: usageTracker.setThreadContext.bind(usageTracker),
    onThreadDetailsChange: (differences, threadDetails) => {
      if (arePropertiesSupportedThreadProps(differences)) {
        evaluateListMembership({
          threadDetails,
          differences
        });
      }
    }
  }), [evaluateListMembership, usageTracker]);
  const vid = getVid(threadListMember);
  const {
    data: visitor
  } = useVisitor({
    vid: vid
  });
  useComposerTrackingContext({
    tracker: usageTracker,
    threadId,
    ticketCount
  });
  return /*#__PURE__*/_jsx(ThreadViewWrapper, {
    AsyncUIAvatar: AsyncUIAvatar,
    auth: auth,
    beaconTracker: getBeaconUsageTracker(),
    inboxId: inboxId,
    lifecycle: lifecycle,
    omnibusData: omnibusData,
    ref: threadViewRef,
    showDeletedMessages: showDeletedMessages
    //@ts-expect-error getChannels is typed incorrectly
    ,
    threadChannels: getChannels(threadListMember),
    threadId: threadId,
    ticketCount: ticketCount,
    tracker: getUsageTracker().tracker,
    vid: vid
    //@ts-expect-error FnF visitor is hand rolled and doesn't match BE type
    ,
    visitor: visitor,
    children: children
  });
};
ThreadViewProvider.displayName = 'ThreadViewProvider';
export default ThreadViewProvider;